// Destination is owned by this project, never by query parameters or Shopify settings.
export const STOREFRONT_ORIGIN = "https://colorsandco.es";

export function readDiscountCookie(cookies = "") {
  const entry = cookies
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith("discount_code="));
  if (!entry) return "";
  try {
    return decodeURIComponent(entry.slice("discount_code=".length));
  } catch {
    return "";
  }
}

export function buildRedirect(
  currentHref,
  familyRoutes,
  { designMode = false, templatePresent = true, pageType = "", discountCode = "" } = {},
) {
  const current = new URL(currentHref);
  if (
    designMode ||
    !templatePresent ||
    current.origin === STOREFRONT_ORIGIN ||
    pageType === "policy" ||
    pageType === "gift_card"
  )
    return null;
  let path;
  try {
    path = decodeURIComponent(current.pathname);
  } catch {
    return null;
  }
  // Shopify owns these flows. Never copy checkout/cart keys or gift-card tokens elsewhere.
  const pathWithoutLocale = path.replace(/^\/[a-z]{2}(?:-[a-z]{2})?(?=\/)/i, "");
  if (
    /^\/(?:policies|checkpoint|challenge|throttle|checkout|checkouts|gift_cards|apps|a|community|tools)(?:\/|$)/i.test(
      pathWithoutLocale,
    ) ||
    /^\/cart\/c(?:\/|$)/i.test(pathWithoutLocale)
  )
    return null;
  let destination = "/";
  const product = pathWithoutLocale.match(/^\/(?:collections\/[^/]+\/)?products\/([a-z0-9-]+)\/?$/);
  if (product && Object.hasOwn(familyRoutes, product[1])) destination = familyRoutes[product[1]];
  else if (/^\/(?:collections|search|cart)(?:\/|$)/.test(pathWithoutLocale))
    destination = "/colors/";
  else if (/^\/pages\/(about|faq|contact|terms)\/?$/.test(pathWithoutLocale))
    destination = `/${pathWithoutLocale.split("/")[2]}/`;
  // Validate even our generated routes: routing data cannot turn into an open redirect.
  if (!/^\/(?:colors\/[a-z0-9-]+\/|colors\/|about\/|faq\/|contact\/|terms\/)?$/.test(destination))
    destination = "/";
  const target = new URL(destination, STOREFRONT_ORIGIN);
  // No variant, cart key, arbitrary redirect, customer token, preview flag, or URL fragment.
  for (const [key, value] of current.searchParams) {
    if (/^utm_(?:source|medium|campaign|term|content)$/.test(key) && value.length <= 256)
      target.searchParams.set(key, value);
  }
  const discount = discountCode || current.searchParams.get("discount") || "";
  if (
    discount &&
    discount.length <= 256 &&
    !Array.from(discount).some(
      (character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
    )
  ) {
    if (target.pathname === "/") target.pathname = "/colors/";
    target.searchParams.set("discount", discount);
  }
  return target.href;
}

export function redirectPage(document, location, shopify = {}) {
  const root = document.documentElement;
  const routes = JSON.parse(document.getElementById("family-routes")?.textContent || "{}");
  const discount = readDiscountCookie(document.cookie);
  const target = buildRedirect(location.href, routes, {
    designMode: root.dataset.designMode === "true" || shopify.designMode === true,
    templatePresent: root.dataset.templatePresent === "true",
    pageType: root.dataset.pageType,
    discountCode: discount,
  });
  if (!target) return;
  const fallback = document.getElementById("storefront-link");
  if (fallback) fallback.href = target;
  if (discount) document.cookie = "discount_code=; Path=/; Max-Age=0; SameSite=Lax; Secure";
  location.replace(target);
}

if (typeof document !== "undefined") redirectPage(document, window.location, window.Shopify || {});
